'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface MediaItem {
  id: number;
  outlet: string;
  headline: string;
  date: string;
  type: 'article' | 'video' | 'podcast';
  reach: string;
  excerpt: string;
}

const mockMediaCoverage: MediaItem[] = [
  {
    id: 1,
    outlet: 'BBC World Service',
    headline: 'The Global Movement Teaching Forgiveness as a Path to Peace',
    date: 'January 15, 2026',
    type: 'video',
    reach: '12.4M views',
    excerpt: 'An in-depth look at how SIG Day is transforming conflict resolution through emotional healing and the radical concept of living with no enemy.'
  },
  {
    id: 2,
    outlet: 'The Guardian',
    headline: 'Can Forgiveness End Wars? This Movement Believes So',
    date: 'December 28, 2025',
    type: 'article',
    reach: '8.7M readers',
    excerpt: 'From Belfast to Dar es Salaam, communities are embracing the no-enemy philosophy with measurable results in conflict reduction.'
  },
  {
    id: 3,
    outlet: 'TED Radio Hour',
    headline: 'The Science and Spirit of Forgiveness',
    date: 'November 10, 2025',
    type: 'podcast',
    reach: '5.2M listeners',
    excerpt: 'Exploring the neuroscience behind forgiveness and how one movement is scaling compassion globally.'
  },
  {
    id: 4,
    outlet: 'Al Jazeera English',
    headline: 'April 17th: The Day the World Chooses Peace',
    date: 'October 22, 2025',
    type: 'video',
    reach: '9.8M views',
    excerpt: 'Documentary following participants across six continents as they prepare for the annual No Enemy Festival.'
  },
  {
    id: 5,
    outlet: 'The New York Times',
    headline: 'Inside the 30-Day Journey to Forgiveness',
    date: 'September 5, 2025',
    type: 'article',
    reach: '11.3M readers',
    excerpt: 'Personal accounts from individuals who transformed their lives through structured forgiveness practices.'
  },
  {
    id: 6,
    outlet: 'NPR Morning Edition',
    headline: 'Humanitarian Impact of the No Enemy Movement',
    date: 'August 17, 2025',
    type: 'podcast',
    reach: '6.9M listeners',
    excerpt: 'How a peace movement is channeling compassion into direct aid for vulnerable populations worldwide.'
  }
];

const MediaCoverage = () => {
  const [selectedType, setSelectedType] = useState<'all' | 'article' | 'video' | 'podcast'>('all');

  const filteredMedia =
    selectedType === 'all'
      ? mockMediaCoverage
      : mockMediaCoverage.filter((item) => item.type === selectedType);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video':
        return 'VideoCameraIcon';
      case 'podcast':
        return 'MicrophoneIcon';
      default:
        return 'NewspaperIcon';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-headline font-bold text-foreground mb-2">
            Global Media Coverage
          </h2>
          <p className="text-base font-body text-muted-foreground">
            International recognition and press mentions
          </p>
        </div>

        <div className="flex items-center space-x-2">
          {(['all', 'article', 'video', 'podcast'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`px-4 py-2 rounded-md font-body text-sm font-medium transition-all duration-200 ${
                selectedType === type
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-primary/10'
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {filteredMedia.map((item) => (
          <div
            key={item.id}
            className="bg-card rounded-lg shadow-md p-6 border border-border hover:shadow-lg transition-all duration-300"
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1 space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon name={getTypeIcon(item.type) as any} size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-xs font-body text-muted-foreground">{item.outlet}</p>
                    <p className="text-xs font-body text-muted-foreground">{item.date}</p>
                  </div>
                </div>

                <h3 className="text-xl font-headline font-bold text-foreground hover:text-primary transition-colors duration-200 cursor-pointer">
                  {item.headline}
                </h3>

                <p className="text-sm font-body text-foreground leading-relaxed">{item.excerpt}</p>

                <div className="flex items-center space-x-4 pt-2">
                  <div className="flex items-center space-x-2 text-sm font-body text-muted-foreground">
                    <Icon name="EyeIcon" size={16} />
                    <span>{item.reach}</span>
                  </div>
                  <button className="text-sm font-body font-semibold text-primary hover:text-primary/80 transition-colors duration-200 flex items-center space-x-1">
                    <span>Read More</span>
                    <Icon name="ArrowRightIcon" size={14} />
                  </button>
                </div>
              </div>

              <div className="flex lg:flex-col items-center lg:items-end space-x-2 lg:space-x-0 lg:space-y-2">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-body font-semibold ${
                    item.type === 'video' ?'bg-error/10 text-error'
                      : item.type === 'podcast' ?'bg-accent/20 text-accent-foreground' :'bg-primary/10 text-primary'
                  }`}
                >
                  {item.type.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MediaCoverage;